// Route-chunk preloading. Vite splits every lazy page into its own chunk; a
// click on a cold link means download → parse → render, which is where the
// loading skeleton comes from. We warm chunks early instead: on hover / focus /
// pointer-down of any nav link (Sidebar, product switcher, bottom tab bar) and,
// after the app is idle, every product home. The import specifiers below are the
// same modules App.tsx lazy-loads, so they resolve to the same chunks.

type Loader = () => Promise<unknown>;

const LOADERS: Array<[prefix: string, load: Loader]> = [
  ["/overview", () => import("@/app/pages/Overview")],
  ["/trade/forecasts", () => import("@/app/pages/forecasts/index")],
  ["/trade/market", () => import("@/app/pages/Market")],
  ["/trade/backtests", () => import("@/app/pages/Backtests")],
  ["/trade/analytics", () => import("@/app/pages/Analytics")],
  ["/trade", () => import("@/app/pages/Dashboard")],
  ["/asset/tickets", () => import("@/app/pages/asset/Tickets")],
  ["/asset/alerts", () => import("@/app/pages/Alerts")],
  ["/asset/sites", () => import("@/app/pages/AssetsList")],
  ["/asset", () => import("@/app/pages/asset/Workspace")],
  ["/health/telemetry", () => import("@/app/pages/health/Telemetry")],
  ["/health/connectors", () => import("@/app/pages/health/Connectors")],
  ["/health/degradation", () => import("@/app/pages/health/Degradation")],
  ["/health/trust", () => import("@/app/pages/health/DataTrust")],
  ["/health", () => import("@/app/pages/asset/Workspace")],
  ["/comply/assessments", () => import("@/app/pages/comply/DsmAssessments")],
  ["/comply/reconciliation", () => import("@/app/pages/comply/Reconciliation")],
  ["/comply/audit-chain", () => import("@/app/pages/comply/AuditChain")],
  ["/comply/scheduling", () => import("@/app/pages/comply/Scheduling")],
  ["/comply/export", () => import("@/app/pages/comply/RegulatorExport")],
  ["/comply", () => import("@/app/pages/comply/ComplyOverview")],
  ["/data/market", () => import("@/app/pages/data/MarketData")],
  ["/data/grid", () => import("@/app/pages/data/GridData")],
  ["/data/settlement", () => import("@/app/pages/data/Settlement")],
  ["/data/exports", () => import("@/app/pages/data/Exports")],
  ["/data", () => import("@/app/pages/data/DataOverview")],
  ["/org/members", () => import("@/app/pages/org/OrgMembers")],
  ["/org/roles", () => import("@/app/pages/org/OrgRoles")],
  ["/org/audit", () => import("@/app/pages/AdminAudit")],
  ["/org", () => import("@/app/pages/org/OrgOverview")],
  ["/settings", () => import("@/app/pages/Settings")],
  ["/admin/tenants", () => import("@/app/pages/AdminTenants")],
  ["/admin/audit", () => import("@/app/pages/AdminAudit")],
];

const warmed = new Set<Loader>();

function loaderFor(path: string): Loader | undefined {
  const clean = path.split(/[?#]/)[0] ?? path;
  // Longest matching prefix wins ("/trade/market" before "/trade").
  for (const [prefix, load] of LOADERS) {
    if (clean === prefix || clean.startsWith(`${prefix}/`)) return load;
  }
  return undefined;
}

/** Warm the chunk for a console path. Safe to call often; each chunk loads once. */
export function preloadRoute(path: string): void {
  const load = loaderFor(path);
  if (!load || warmed.has(load)) return;
  warmed.add(load);
  load().catch(() => warmed.delete(load));
}

/** Props to spread on a link so hover / focus / touch-start warm its chunk. */
export function preloadHandlers(path: string) {
  const warm = () => preloadRoute(path);
  return { onMouseEnter: warm, onFocus: warm, onPointerDown: warm, onTouchStart: warm } as const;
}

const PRODUCT_HOMES = ["/overview", "/trade", "/asset", "/health/telemetry", "/comply", "/data", "/org", "/settings"];

/** After first paint and an idle moment, warm every product home in the background. */
export function preloadProductHomes(): () => void {
  let cancelled = false;
  const run = () => {
    if (cancelled) return;
    PRODUCT_HOMES.forEach((p, i) => window.setTimeout(() => !cancelled && preloadRoute(p), i * 120));
  };
  const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
  let idleId: number | undefined;
  const timer = window.setTimeout(() => {
    if (w.requestIdleCallback) idleId = w.requestIdleCallback(run, { timeout: 2500 });
    else run();
  }, 1200);
  return () => {
    cancelled = true;
    window.clearTimeout(timer);
    if (idleId !== undefined && w.cancelIdleCallback) w.cancelIdleCallback(idleId);
  };
}
